import { serverURL } from '@/utils/serverURL';
import type {
  ImportParseResult,
  ImportCommitMode,
  ImportSelection,
  ParseSource,
} from '@/utils/types/flavorImport';
import type { VersionDetail } from '@/utils/types/flavorVersion';

export async function parseImport(
  flavorId: string,
  formulaId: number,
  source: ParseSource,
): Promise<ImportParseResult> {
  const res = await fetch(
    serverURL`/me/flavor/${flavorId}/formulas/${formulaId}/import/parse`,
    {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(source),
    },
  );
  if (!res.ok) throw new Error(`parseImport failed: HTTP ${res.status}`);
  return (await res.json()) as ImportParseResult;
}

export async function commitImport(
  flavorId: string,
  formulaId: number,
  args: { parseId: string; mode: ImportCommitMode; selections: ImportSelection[] },
): Promise<VersionDetail> {
  const res = await fetch(
    serverURL`/me/flavor/${flavorId}/formulas/${formulaId}/import/commit`,
    {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(args),
    },
  );
  if (!res.ok) throw new Error(`commitImport failed: HTTP ${res.status}`);
  return (await res.json()) as VersionDetail;
}

// Presign + upload helpers for the import_formulation file-upload flow.
// These are NOT hooks — they are procedural helpers the modal calls directly
// before invoking parseImport({ kind: 'file', fileUploadId }).
//
// The existing uploads_api.ts presignUpload hardcodes kind: 'chromatogram',
// so this provides a parallel helper for kind: 'import_formulation'.
// The presign response is snake_case (confirmed from server controller).

export type PresignImportResponse = {
  fileUploadId: string;
  presignedUrl: string;
  /** Headers the browser must round-trip on the S3 PUT (currently just Content-Type). */
  requiredHeaders: { 'Content-Type': string };
};

export async function presignImportFile(args: {
  // Optional: the workbench import passes the formula id; the New-Flavor
  // pre-create import has no flavor/formula yet and omits it (server stores it
  // only in metadata, never reads it back).
  flavorFormulaId?: number;
  filename: string;
  contentType: string;
  sizeBytes: number;
  signal?: AbortSignal;
}): Promise<PresignImportResponse> {
  const res = await fetch(serverURL`/me/flavor/uploads/presign`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      kind: 'import_formulation',
      ...(args.flavorFormulaId != null
        ? { flavor_formula_id: args.flavorFormulaId }
        : {}),
      filename: args.filename,
      content_type: args.contentType,
      size_bytes: args.sizeBytes,
    }),
    signal: args.signal,
  });
  if (!res.ok) throw new Error(`presignImportFile failed: HTTP ${res.status}`);
  const data = (await res.json()) as {
    file_upload_id: string;
    presigned_url: string;
    required_headers?: { 'Content-Type': string };
  };
  return {
    fileUploadId: data.file_upload_id,
    presignedUrl: data.presigned_url,
    requiredHeaders: data.required_headers ?? { 'Content-Type': args.contentType },
  };
}
